import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Tpl3plService } from '../../../services/tpl3pl.service';

export interface AttachSavedCardToOrderDialogData {
	/** When set, load/attach via shipment (3PL shipments UI). */
	shipmentId?: number;
	orderno: string;
	customerName: string;
	tpl3plService: Tpl3plService;
	/** After attach, run pre-export pre-auth (full open balance). Used from 3PL export queue. */
	runPreauthAfterAttach?: boolean;
}

@Component({
	selector: 'app-attach-saved-card-to-order-dialog',
	template: `
		<h2 mat-dialog-title>{{ data.runPreauthAfterAttach ? 'Attach card & pre-authorize' : 'Attach saved card to order' }}</h2>
		<mat-dialog-content class="ascd-content">
			<p class="ascd-meta">
				Order <strong>{{ data.orderno }}</strong>
				<span *ngIf="data.customerName"> — {{ data.customerName }}</span>
			</p>
			<p *ngIf="data.runPreauthAfterAttach" class="ascd-hint small text-muted">
				Picks a card on file for this customer, attaches it to the order, then places a hold for the open order total (same as export-queue pre-auth).
			</p>
			<mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>
			<p *ngIf="error" class="ascd-err">{{ error }}</p>
			<p *ngIf="currentSummary" class="ascd-current">Card on order now: {{ currentSummary }}</p>
			<p *ngIf="!loading && !error && cards.length === 0" class="ascd-empty">No saved cards on file for this customer and branch.</p>
			<mat-radio-group *ngIf="!loading && cards.length > 0" class="ascd-radio" [(ngModel)]="selectedCardId">
				<mat-radio-button *ngFor="let c of cards" [value]="c.card_id" class="ascd-opt">{{ c.label }}</mat-radio-button>
			</mat-radio-group>
		</mat-dialog-content>
		<mat-dialog-actions align="end">
			<button mat-button type="button" mat-dialog-close>Cancel</button>
			<button
				mat-flat-button
				color="primary"
				type="button"
				[disabled]="loading || saving || !selectedCardId || !!error"
				(click)="attach()"
			>
				{{ data.runPreauthAfterAttach ? 'Attach & pre-auth' : 'Attach to order' }}
			</button>
		</mat-dialog-actions>
	`,
	styles: [
		`
			.ascd-content {
				min-width: 300px;
				max-width: 420px;
			}
			.ascd-meta {
				margin: 0 0 8px;
				font-size: 0.9rem;
			}
			.ascd-hint {
				margin: 0 0 10px;
				line-height: 1.35;
			}
			.ascd-current {
				margin: 0 0 10px;
				font-size: 0.82rem;
				color: rgba(0, 0, 0, 0.6);
			}
			.ascd-err {
				color: #c62828;
				font-size: 0.85rem;
			}
			.ascd-empty {
				margin: 8px 0 0;
				font-size: 0.9rem;
			}
			.ascd-radio {
				display: flex;
				flex-direction: column;
				gap: 6px;
				margin-top: 8px;
			}
			.ascd-opt {
				height: auto;
				white-space: normal;
				line-height: 1.35;
			}
		`
	]
})
export class AttachSavedCardToOrderDialogComponent implements OnInit {
	loading = true;
	saving = false;
	error: string | null = null;
	cards: Array<{ card_id: string; label: string }> = [];
	selectedCardId: string | null = null;
	currentSummary = '';

	constructor(
		private dialogRef: MatDialogRef<AttachSavedCardToOrderDialogComponent, { success: boolean; message?: string; preauth_ok?: boolean }>,
		@Inject(MAT_DIALOG_DATA) public data: AttachSavedCardToOrderDialogData
	) {}

	ngOnInit(): void {
		const useShipment = this.data.shipmentId != null && this.data.shipmentId > 0;
		const load$ = useShipment
			? this.data.tpl3plService.getShipmentCustomerCards(this.data.shipmentId!)
			: this.data.tpl3plService.getExportQueueOrderCustomerCards(this.data.orderno);
		load$.subscribe({
			next: (res) => {
				this.loading = false;
				if (!res?.success) {
					this.error = res?.message || 'Could not load saved cards';
					return;
				}
				this.cards = Array.isArray(res.cards) ? res.cards : [];
				const cur = res.current_order_card;
				if (cur && (cur.card_id || cur.card_number || cur.card_type)) {
					const bits = [cur.card_type, cur.card_number, cur.name].filter((x) => x && String(x).trim() !== '');
					this.currentSummary = bits.join(' · ');
				}
				if (this.cards.length === 1) {
					this.selectedCardId = this.cards[0].card_id;
				}
			},
			error: (err) => {
				this.loading = false;
				this.error = err?.error?.message || err?.message || 'Could not load saved cards';
			}
		});
	}

	attach(): void {
		if (!this.selectedCardId) {
			return;
		}
		this.saving = true;
		this.error = null;
		const useShipment = this.data.shipmentId != null && this.data.shipmentId > 0;
		const attach$ = useShipment
			? this.data.tpl3plService.attachSavedCardToShipmentOrder(this.data.shipmentId!, this.selectedCardId)
			: this.data.tpl3plService.attachSavedCardToOrderByOrderno(this.data.orderno, this.selectedCardId);
		attach$.subscribe({
			next: (res) => {
				if (!res?.success) {
					this.saving = false;
					this.error = res?.message || 'Attach failed';
					return;
				}
				const attachMsg = res.message || 'Saved card attached to order.';
				if (!this.data.runPreauthAfterAttach) {
					this.saving = false;
					this.dialogRef.close({ success: true, message: attachMsg });
					return;
				}
				this.data.tpl3plService.reauthorizePreExport(this.data.orderno).subscribe({
					next: (pre) => {
						this.saving = false;
						const preOk = !!(pre && typeof pre === 'object' && pre.success);
						const preMsg = preOk ? (pre.message || 'Pre-authorized.') : (pre?.message || 'Pre-authorization failed');
						this.dialogRef.close({
							success: true,
							message: attachMsg + ' ' + preMsg,
							preauth_ok: preOk,
						});
					},
					error: (err) => {
						this.saving = false;
						this.dialogRef.close({
							success: true,
							message: attachMsg + ' Pre-auth: ' + (err?.error?.message || err?.message || 'failed'),
							preauth_ok: false,
						});
					},
				});
			},
			error: (err) => {
				this.saving = false;
				this.error = err?.error?.message || err?.message || 'Attach failed';
			},
		});
	}
}
